import { NAVIGATION_ROUTES } from "./NavigationUtils"

/**
 * Shape of a backend i18n catalog key (`pam.error.user_not_found`,
 * `users.success.referrer_bonus_blocked`). Those are lookup keys, not copy: if
 * one leaks into the `message` field — an unlocalized code path, a catalog
 * miss — rendering it would put a dotted identifier in a toast. Treating it as
 * "no message" keeps the invariant that what we display is always prose.
 */
const MSG_CODE_SHAPE = /^[a-z0-9_]+(\.[a-z0-9_-]+){1,3}$/;

/**
 * A candidate value reduced to displayable prose, or `""` when it is missing,
 * blank, the wrong type, or a leaked message code.
 *
 * @param {unknown} value
 * @returns {string}
 */
const displayableText = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const text = value.trim();
  if (!text || MSG_CODE_SHAPE.test(text)) {
    return "";
  }
  return text;
};

/**
 * User-facing text from axios/API errors (e.g. admin-panel-payments error bodies).
 * Prefers top-level `message`, then a string `error`, then a structured
 * `error.message` (the `{ code, message, details }` envelope used by
 * admin-panel-casino), then string/FastAPI `detail`.
 *
 * trivelta-admin-services localizes `message` server-side, so surfacing it
 * verbatim is what keeps request-outcome copy translatable without a
 * frontend catalog of its own.
 *
 * @param {unknown} error - Typically an axios error with `response.data`
 * @param {string} fallback - Shown when no message can be extracted
 * @returns {string}
 */
export const getApiErrorMessage = (error, fallback = "") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return fallback;
  }

  const topLevel = displayableText(data.message);
  if (topLevel) {
    return topLevel;
  }

  const stringError = displayableText(data.error);
  if (stringError) {
    return stringError;
  }

  // Structured error envelope: { success: false, error: { code, message } }
  if (data.error && typeof data.error === "object") {
    const nested = displayableText(data.error.message);
    if (nested) {
      return nested;
    }
  }

  const { detail } = data;
  const stringDetail = displayableText(detail);
  if (stringDetail) {
    return stringDetail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    const firstText = displayableText(first);
    if (firstText) {
      return firstText;
    }
    const firstMsg = displayableText(first?.msg);
    if (firstMsg) {
      return firstMsg;
    }
  }

  return fallback;
};

/**
 * User-facing text from a successful API response. The backend renders a
 * localized `message` on every success envelope, so a toast reporting the
 * outcome of a request should surface that rather than hardcode English.
 *
 * Accepts either an axios response (`data.message`) or an already-unwrapped
 * envelope (`message`) — the react-query mutation layer in `src/queries`
 * returns `response.data`, so both reach toast handlers.
 *
 * @param {unknown} response - An axios response or a `{ success, data, message }` envelope
 * @param {string} fallback - Shown when the response carries no message
 * @returns {string}
 */
export const getApiSuccessMessage = (response, fallback = "") => {
  if (!response || typeof response !== "object") {
    return fallback;
  }

  const unwrapped = displayableText(response.message);
  if (unwrapped) {
    return unwrapped;
  }

  const { data } = response;
  if (data && typeof data === "object") {
    const nested = displayableText(data.message);
    if (nested) {
      return nested;
    }
  }

  return fallback;
};

/**
 * Codes the gamepass API would use for the "season has nothing completable"
 * activation guard. Provisional: as of writing the endpoint returns only prose,
 * so the substring fallback below is what actually fires. Kept first so the
 * moment a code ships, matching stops depending on wording.
 */
const FORCE_ACTIVATE_BLOCKER_CODES = new Set([
  "FORCE_ACTIVATE_REQUIRED",
  "SEASON_ACTIVATION_BLOCKED",
  "SEASON_MISSING_TASKS",
  "SEASON_HAS_NO_COMPLETABLE_TASKS",
]);

/**
 * The error code from any of the envelope shapes that carry one, normalized to
 * the bare `SCREAMING_SNAKE` form so `pam.error.season_missing_tasks` and
 * `SEASON_MISSING_TASKS` compare equal.
 *
 * @param {unknown} error
 * @returns {string}
 */
const getApiErrorCode = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return "";
  }
  const raw =
    data.error_code ??
    (typeof data.error === "object" ? data.error?.code : undefined) ??
    data.code ??
    data.msg_code;
  if (typeof raw !== "string" || !raw.trim()) {
    return "";
  }
  const segments = raw.trim().split(".");
  return segments[segments.length - 1].replace(/-/g, "_").toUpperCase();
};

/**
 * Whether an activate-season failure is the backend's "season has no
 * completable tasks" guard, which the UI recovers from by offering a
 * force-activate. Single source of truth for both the Seasons list and the
 * Season Details page so the error vocabulary only lives in one place.
 *
 * Prefers the structured code; the substring matching is the dual-form
 * fallback for the current gamepass responses, which carry prose only.
 *
 * @param {unknown} error - Typically an axios error with `response.data`
 * @returns {boolean}
 */
export const shouldOfferForceActivate = (error) => {
  if (FORCE_ACTIVATE_BLOCKER_CODES.has(getApiErrorCode(error))) {
    return true;
  }

  const messageText = getApiErrorMessage(error, "").toLowerCase();
  return (
    messageText.includes("force") ||
    messageText.includes("at least one task") ||
    (messageText.includes("challenge") && messageText.includes("task"))
  );
};

const EXPIRED_TOKEN_STATUS = 401;

/**
 * Sends the admin back to login when a request failed because the session is
 * gone. Keys on the 401 status; the top-level `message` substring check is
 * retained as the dual-form fallback for callers that hand over a plain
 * `{ message }` object rather than an axios error or response.
 *
 * Call sites disagree on argument order — most pass `(navigate, response)`,
 * StaffDetails passes `(response, navigate)` — so the arguments are matched by
 * type instead of position, which would silently no-op for one of the forms.
 *
 * @param {...unknown} args - A navigate function and an axios error/response
 * @returns {void}
 */
export const handleExpiredTokenError = (...args) => {
  const navigate = args.find((arg) => typeof arg === "function");
  if (!navigate) {
    return;
  }
  const errorLike = args.find((arg) => arg && typeof arg === "object");

  const status = errorLike?.status ?? errorLike?.response?.status;
  const { message } = errorLike ?? {};

  if (
    status === EXPIRED_TOKEN_STATUS ||
    (typeof message === "string" && message.includes("expire"))
  ) {
    navigate(NAVIGATION_ROUTES?.LOGIN);
  }
};
